import { TypedDocumentNode, gql } from '@apollo/client';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { Rarity, Sport } from '@sorare/core/src/__generated__/globalTypes';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { FOOTBALL_PLAY_PRO_ONBOARDING_TUTORIAL } from '@sorare/core/src/constants/__generated__/routes';
import { useMutation } from '@sorare/core/src/hooks/graphql/useMutation';
import { useGetInteractionContext } from '@sorare/core/src/hooks/useGetInteractionContext';

import type {
  forceLimitedOnboardingUnlockProModeMutation,
  forceLimitedOnboardingUnlockProModeMutationVariables,
} from './__generated__/index.graphql';

const UNLOCK_PRO_MODE_MUTATION = gql`
  mutation forceLimitedOnboardingUnlockProModeMutation(
    $input: unlockProModeInput!
  ) {
    unlockProMode(input: $input) {
      currentUser {
        slug
        forcedIntoOnboarding
        anyUserSportProfile(sport: FOOTBALL) {
          id
          ... on FootballUserSportProfile {
            id
            proUnlocked
          }
        }
      }
      errors {
        message
        code
      }
    }
  }
` as TypedDocumentNode<
  forceLimitedOnboardingUnlockProModeMutation,
  forceLimitedOnboardingUnlockProModeMutationVariables
>;

export const ForceLimitedOnboarding = () => {
  const navigate = useNavigate();
  const getInteractionContext = useGetInteractionContext();
  const [mutate, { loading }] = useMutation(UNLOCK_PRO_MODE_MUTATION, {
    showErrorsWithSnackNotification: true,
  });
  useEffect(() => {
    (async () => {
      await mutate({
        variables: {
          input: {
            sport: Sport.FOOTBALL,
            rarity: Rarity.limited,
            interactionContext: getInteractionContext(),
          },
        },
      });
      navigate(
        `${FOOTBALL_PLAY_PRO_ONBOARDING_TUTORIAL}?rarity=${Rarity.limited}`,
        {
          replace: true,
        }
      );
    })();
  }, [navigate, getInteractionContext, mutate]);

  return loading && <LoadingIndicator fullScreen />;
};
